import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import { useContext, useContextSelector } from 'use-context-selector';
import { getContextPreloadedDataByProperties } from '../../utils/getContextPreloadedDataByProperties';
import { ProcessEngineWizardContext } from './ProcessEngineWizardContext';

const useWizardState = () => {
  const context = useContext(ProcessEngineWizardContext);

  if (context === undefined) {
    throw new Error('useWizardState must be used within WizardProvider');
  }

  return context;
};

const useWizardErrorState = () => {
  const context = useContextSelector(ProcessEngineWizardContext, state => state.networkCallErrors);

  if (context === undefined) {
    throw new Error('useWizardErrorState must be used within WizardProvider');
  }

  return context;
};

const useSetWizardErrorState = () => {
  const context = useContextSelector(ProcessEngineWizardContext, state => state.setNetworkCallErrors);

  return context;
};

const useWizardCurrentProcessState = () => {
  const context = useContextSelector(ProcessEngineWizardContext, state => state.currentProcessState);

  if (context === undefined) {
    throw new Error('useWizardPreloadedData must be used within WizardProvider');
  }

  return context;
};

const useWizardResponseData = ({ stepId }: { stepId?: string }): GenericObject => {
  const context = useContextSelector(ProcessEngineWizardContext, state =>
    state.currentProcessState.stepId === stepId || stepId === undefined
      ? state.currentProcessState.responseData?.data
      : state.currentProcessState.previousResponseData?.data,
  );

  if (context === undefined) {
    throw new Error('useWizardCurrentStepFormConfig must be used within WizardProvider');
  }

  return context;
};

const useWizardCurrentFormConfig = (): GenericObject => {
  const context = useContextSelector(
    ProcessEngineWizardContext,
    state => state.currentProcessState.responseData?.parameters,
  );

  if (context === undefined) {
    throw new Error('useWizardCurrentStepFormConfig must be used within WizardProvider');
  }

  return context;
};

/**
 *
 * @param {String | String[]} stepId optionally, the given step's id which the form data we want from.
 * If we provide the hook with an array of step ids, then it returns the corresponding steps' data.
 * @returns if given a step id, it returns the step's form data, otherwise it returns the whole process state
 */
const useWizardPreloadedFormData = (stepId?: string): Nullable<GenericObject> => {
  const currentProcessState = useContextSelector(ProcessEngineWizardContext, state => state.currentProcessState);
  let context = useContextSelector(ProcessEngineWizardContext, state => state.currentProcessState.formData);
  if (context === undefined) {
    throw new Error('useWizardPreloadedFormData must be used within WizardProvider');
  }
  if (context === null) {
    return context;
  }
  if (stepId) {
    context = context[stepId];
  } else {
    context = context[currentProcessState.stepId as string];
  }
  return context as any;
};

const useWizardPreloadedStepData = (
  properties?: string | string[],
): Nullable<GenericObject | string | number | null> => {
  const context = useContextSelector(ProcessEngineWizardContext, state => state.preloadedData);
  if (context === undefined) {
    throw new Error('useWizardPreloadedData must be used within WizardProvider');
  }

  if (context === null) {
    return context;
  }

  if (Array.isArray(properties) && !isEmpty(properties)) {
    return getContextPreloadedDataByProperties(properties, context);
  }
  if (properties) {
    return context?.[properties as string];
  }

  return context;
};

const useWizardGoToNextStep = () => {
  const context = useContextSelector(ProcessEngineWizardContext, state => state.goToNextWizardStep);

  if (context === undefined) {
    throw new Error('useWizardGoToNextStep must be used within WizardProvider');
  }

  return context;
};

const useWizardGoToPreviousStep = () => {
  const context = useContextSelector(ProcessEngineWizardContext, state => state.goToPreviousWizardStep);

  if (context === undefined) {
    throw new Error('useWizardGoToPreviousStep must be used within WizardProvider');
  }

  return context;
};

const useWizardActionStep = (actionType: string) => {
  const context = useContextSelector(ProcessEngineWizardContext, state => () => state.actionWizardStep(actionType));

  if (context === undefined) {
    throw new Error('useWizardActionStep must be used within WizardProvider');
  }

  return context;
};

const useWizardCurrentStepConfiguration = () => {
  const context = useContextSelector(ProcessEngineWizardContext, state => state.currentStepConfig);

  if (context === undefined) {
    throw new Error('useWizardCurrentStepConfiguration must be used within WizardProvider');
  }

  return context;
};

const useWizardProcessLoading = () => {
  const context = useContextSelector(ProcessEngineWizardContext, state => state.currentProcessState.isLoading);

  if (context === undefined) {
    throw new Error('useWizardProcessLoading must be used within WizardProvider');
  }

  return context;
};

export {
  useWizardCurrentProcessState,
  useWizardPreloadedFormData,
  useWizardPreloadedStepData,
  useWizardCurrentStepConfiguration,
  useWizardState,
  useWizardGoToNextStep,
  useWizardGoToPreviousStep,
  useWizardActionStep,
  useWizardResponseData,
  useWizardCurrentFormConfig,
  useWizardProcessLoading,
  useWizardErrorState,
  useSetWizardErrorState,
};
